import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RANGE_PRESETS, matchPreset, resolvePreset, type RangePreset } from '@construction/shared';
import { Button, Field, Input } from '@/components/ui';
import { usePopoverPlacement } from '@/hooks/usePopoverPlacement';
import { cn } from '@/lib/cn';
import { chipClass } from './FilterChip';

/**
 * 区间筛选：平时收成一条，点开才是面板（CR-034）。
 *
 * 客户给了两张图：收起来是一条带日历图标的横条，写着当前区间；点一下弹出面板，
 * 里面是起止日期、一排快捷按钮、右下角「确认」。四个列表页统一用这个。
 *
 * **面板里的改动先进草稿，点「确认」才生效。** 直接改起始日期那一下就去查一次的话，
 * 中间会用一个半截区间（新起点 + 旧终点）拉一次数据，列表闪一下再闪回来 ——
 * 而那一帧的数字是错的，正在对账的人会当真。
 *
 * 快捷按钮也只写草稿：两套规则并存（按钮立即生效、手输要确认）会让人不知道
 * 自己到底选中没有。
 *
 * 筛选仍然是**日期粒度**。客户问过要不要精确到分钟，结论是那属于「这条记录
 * 什么时候进的系统」，由 CR-033 的「提交于 / 拍摄于」回答，不归筛选管。
 *
 * CR-082：收起时是一个筛选胶囊「日期 本周」，和旁边的「类别 全部」长得一样；不是快捷区间时写起止日期。
 * 日历图标去掉了（客户不要图标，CR-081）。
 */
export function RangeField({
  from,
  to,
  today,
  presets = RANGE_PRESETS,
  label: chipLabel,
  onChange,
}: {
  from: string;
  to: string;
  /** 工地当地的今天（YYYY-MM-DD）。用浏览器的今天会在跨时区时差一天 */
  today: string;
  presets?: readonly RangePreset[];
  /** 胶囊前面的灰字，默认「日期」 */
  label?: string;
  onChange: (range: { from: string; to: string }) => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from, to });
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // 面板往哪开、多高、横向挪多少按可视区算，不写死往下
  usePopoverPlacement(open, rootRef, panelRef);

  // 外面改了区间（比如换工地重置）要跟着走，否则下次点开看到的是上一轮的草稿
  useEffect(() => {
    setDraft({ from, to });
  }, [from, to]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    // 关掉时丢弃草稿：没点「确认」就是没改
    function close() {
      setDraft({ from, to });
      setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, from, to]);

  const label = (key: string) =>
    new Date(`${key}T12:00:00Z`).toLocaleDateString(i18n.language, { timeZone: 'UTC' });
  // 胶囊上写短日期（9/21），面板里的日期框照旧是完整的
  const short = (key: string) =>
    new Date(`${key}T12:00:00Z`).toLocaleDateString(i18n.language, { timeZone: 'UTC', month: 'numeric', day: 'numeric' });

  const active = matchPreset({ from, to }, today);
  const draftActive = matchPreset(draft, today);

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${chipLabel ?? t('filters.date')}: ${label(from)} ~ ${label(to)}`}
        className={chipClass(open)}
      >
        <span className="text-ink-3">{chipLabel ?? t('filters.date')}</span>
        <span className="font-semibold text-ink tabular-nums">
          {active ? t(`filters.preset.${active}`) : from === to ? short(from) : `${short(from)} – ${short(to)}`}
        </span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('filters.rangeLabel')}
          // z-dropdown：它盖在页面内容上，但要让位给抽屉和全屏查看器（index.css 的层级表）。
          // 上下位置、左偏移、最大宽高由 usePopoverPlacement 写上去。改之前写死
          // `top-full` + `max-w-[calc(100vw-4rem)]`，前提是按钮贴着卡片左边 —— 采购、考勤页按钮前面
          // 有「日期范围」，手机上面板右边出屏 37px；按钮在屏幕下半截时往下出屏几百像素
          className="absolute left-0 z-dropdown w-[24rem] rounded-xl border border-rule-strong bg-popover p-4 shadow-pop"
        >
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-36 flex-1">
              <Field label={t('filters.from')} htmlFor="rf-from">
                <Input
                  id="rf-from"
                  type="date"
                  value={draft.from}
                  max={draft.to}
                  onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                />
              </Field>
            </div>
            <span aria-hidden className="pb-2 text-ink-3">
              ~
            </span>
            <div className="min-w-36 flex-1">
              <Field label={t('filters.to')} htmlFor="rf-to">
                <Input
                  id="rf-to"
                  type="date"
                  value={draft.to}
                  min={draft.from}
                  onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                />
              </Field>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {presets.map((preset) => {
              const range = resolvePreset(preset, today);
              const selected = draftActive === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setDraft(range)}
                  className={cn(
                    'flex min-h-touch-sm items-center rounded-full border px-3 text-body transition-colors',
                    selected
                      ? 'border-brand-500 bg-brand-50 font-semibold text-brand-700'
                      : 'border-rule-strong text-ink-2 hover:bg-ink/6',
                  )}
                >
                  {t(`filters.preset.${preset}`)}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setDraft({ from, to });
                setOpen(false);
              }}
            >
              {t('app.cancel')}
            </Button>
            <Button
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
            >
              {t('filters.apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
